import { Suspense, lazy } from "react";
import { Link } from "react-router-dom";
import { m } from "framer-motion";
import { Icon } from "@/components/Icon";
import AnonymousCapture from "@/components/landing/AnonymousCapture";
import ClipPreviewPanel from "@/components/marketing/ClipPreviewPanel";
import { FEATURED_CLIP } from "@/components/marketing/featuredClip";
import ClosingCTA from "@/components/marketing/ClosingCTA";
import FrontDoorSection from "@/components/marketing/FrontDoorSection";
import HeroUsageBadge from "@/components/marketing/HeroUsageBadge";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import PillarCards from "@/components/marketing/PillarCards";
import { track } from "@/lib/analytics";
import WorkspaceLink from "@/components/public/WorkspaceLink";

const ClipGallery = lazy(() => import("@/components/landing/ClipGallery"));

const TESTFLIGHT_URL = "https://testflight.apple.com/join/4Ym7X41D";
const EASE = [0.22, 1, 0.36, 1] as const;

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.58, ease: EASE } },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
};

export default function LandingPage() {
  return (
    <MarketingLayout active="home">
      <header className="relative px-6 pb-[60px] pt-28 md:px-8 md:pt-32">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute left-1/2 top-0 h-[640px] w-[940px] -translate-x-1/2 bg-[radial-gradient(ellipse_at_center,rgba(46,115,184,0.16),transparent_65%)]"
        />
        <div className="relative mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-[1.15fr_0.85fr]">
          <m.div variants={stagger} initial="hidden" animate="show">
            {/* Live count from GET /public/stats (backend#251). Falls back to
                a no-number capability sentence — never the invented 41,208. */}
            <HeroUsageBadge variants={fadeUp} />

            <m.h1
              variants={fadeUp}
              className="mt-[22px] text-balance text-5xl font-extrabold leading-[1.02] tracking-[-0.032em] text-ios sm:text-6xl lg:text-[62px]"
            >
              {/* The design's three-line break only works at desktop widths;
                  below lg the copy wraps on its own. */}
              Share a link.
              <br className="hidden lg:block" />{" "}
              Get back a page
              <br className="hidden lg:block" />{" "}
              worth sharing.
            </m.h1>

            <m.p variants={fadeUp} className="mt-5 max-w-[460px] text-lg leading-normal text-ios-secondary">
              ManticsCore reads every link you save, writes it up, files it into a thread on its own,
              and hands you a public page you can send to anyone. No folders. No tagging. No page
              builder.
            </m.p>

            <m.div variants={fadeUp} className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <a
                href={TESTFLIGHT_URL}
                target="_blank"
                rel="noreferrer"
                onClick={() => track("testflight_clicked", { location: "hero" })}
                className="inline-flex min-h-12 items-center justify-center gap-2.5 rounded-[26px] bg-ios-accent px-7 text-[15px] font-semibold text-white shadow-[0_4px_20px_rgba(46,115,184,0.35)] transition-opacity hover:opacity-90"
              >
                <Icon name="phone_iphone" size="sm" />
                Get it free on iOS
              </a>
              {/* Straight to the clip the panel beside this depicts. */}
              <Link
                to={`/clip/${FEATURED_CLIP.slug}`}
                onClick={() => track("web_capture_link_clicked", { location: "hero_see_clip" })}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-[26px] border border-white/[0.16] px-6 text-[14.5px] font-semibold text-[#DEDEDE] transition-colors hover:border-white/30 hover:text-ios"
              >
                See a live clip &#8599;
              </Link>
            </m.div>

            <m.p variants={fadeUp} className="mt-4 text-[12.5px] text-ios-subtle">
              Free while we&rsquo;re in TestFlight &middot; No account needed to read a clip
            </m.p>
            <m.div variants={fadeUp} className="mt-5 text-sm text-ios-secondary">
              On your computer?{" "}
              <WorkspaceLink signedOutLabel="Continue on the web" className="font-semibold text-ios-accent-text underline underline-offset-4" />
              <p className="mt-1 text-xs text-ios-muted">The same account, threads, and published clips as on iPhone.</p>
            </m.div>
          </m.div>

          <ClipPreviewPanel />
        </div>
      </header>

      <PillarCards />

      <FrontDoorSection />

      <AnonymousCapture />

      <Suspense
        fallback={
          <div className="mx-auto h-[420px] max-w-6xl animate-pulse rounded-2xl bg-white/[0.025]" aria-hidden="true" />
        }
      >
        <ClipGallery />
      </Suspense>

      <ClosingCTA
        title="Stop bookmarking. Start publishing."
        body="No account needed to read a clip. Send one and see what comes back."
        cta="Get it free on iOS"
        location="closing"
      />
    </MarketingLayout>
  );
}
